import { Activity, WifiOff, Thermometer, Droplets, Waves, ToggleLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { SensorDevice } from '../../services/sensorDevices';

interface MultiChannelSensorCardProps {
  device: SensorDevice;
}

const ICON_MAP: Record<string, React.ReactNode> = {
  temperature: <Thermometer className="w-3.5 h-3.5 text-orange-500" />,
  humidity: <Droplets className="w-3.5 h-3.5 text-sky-500" />,
  water_level: <Waves className="w-3.5 h-3.5 text-blue-500" />,
  tank_level: <Droplets className="w-3.5 h-3.5 text-blue-600" />,
  digital_input: <ToggleLeft className="w-3.5 h-3.5 text-gray-500" />,
  generic_adc: <Activity className="w-3.5 h-3.5 text-violet-500" />,
};

export default function MultiChannelSensorCard({ device }: MultiChannelSensorCardProps) {
  const channels = device.channels ?? [];
  const latestReadings = device.latestReadings ?? [];

  function getLatest(kind: string, index: number) {
    return latestReadings.find(
      r => r.channel_index === index && r.channel_kind === kind
    );
  }

  return (
    <Link
      to={`/irrigation/sensors/${encodeURIComponent(device.device_id)}`}
      className="block rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Activity className="w-4 h-4 text-violet-600 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800 truncate">
              {device.name || device.device_id}
            </div>
            <div className="text-xs text-gray-500">
              {channels.length} channel{channels.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
        {!device.online && <WifiOff className="w-4 h-4 text-gray-400" />}
      </div>

      {channels.length === 0 ? (
        <div className="text-xs text-gray-400 italic">No channels configured</div>
      ) : (
        <div className="space-y-1.5">
          {channels.map(ch => {
            const latest = getLatest(ch.channel_kind, ch.channel_index);
            const value = latest?.value;
            return (
              <div key={`${ch.channel_kind}-${ch.channel_index}`}
                className="flex items-center justify-between gap-2 text-sm"
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  {ICON_MAP[ch.type_key] || <Activity className="w-3.5 h-3.5 text-gray-400" />}
                  <span className="text-gray-600 truncate text-xs">
                    {ch.name || `Ch ${ch.channel_index}`}
                  </span>
                </div>
                <div className="font-semibold text-gray-800 text-sm">
                  {value !== null && value !== undefined
                    ? `${Number(value).toFixed(1)}${ch.type_unit ? ' ' + ch.type_unit : ''}`
                    : '—'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {device.last_seen && (
        <div className="mt-2 text-[11px] text-gray-400">
          Last: {new Date(device.last_seen).toLocaleString()}
        </div>
      )}
    </Link>
  );
}

import React from 'react';
